import {USER_KEYS} from "@/api/userApi";
import Loader from "@/components/Common/kpi/Loader";
import type {NotificationSerializer} from "@/generated/DjangoTypes";
import {useInViewWithQuery} from "@/hooks/useInViewWithQuery";
import useNotificationType from "@/hooks/useNotificationType";
import {useGenericInfiniteQuery} from "@/hooks/useQueryHooks";
import {useUserId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import useNotificationStore from "@/store/notification";
import type {TNotificationType} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {Link} from "@tanstack/react-router";
import dayjs from "dayjs";
import {Fragment, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useEffectOnce} from "react-use";

type TNotification = Omit<NotificationSerializer, "type"> & {
  type: TNotificationType;
  translation_params?: Record<string, string> | null;
};

const NotificationsTab: FC = () => {
  const {t} = useTranslation("notifications");
  const mapNotification = useNotificationType();
  const resetNotificationCounts = useNotificationStore(state => state.resetNotificationCounts);
  const toggleOpen = useNotificationStore(state => state.toggleOpen);
  const unseenNotificationCount = useNotificationStore(state => state.notificationCounts.notification.unseen);
  const userId = useUserId();
  const {mutate: markAllAsSeen} = useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserNotificationListSeenStatus(userId),
    mutationFn: () => client.put(USER_KEYS.mUserNotificationListSeenStatus(userId)[0]),
    onMutate: resetNotificationCounts,
  });

  const notificationsQuery = useGenericInfiniteQuery<TNotification>({
    queryKey: USER_KEYS.qUserNotificationList(userId),
    enabled: !!userId,
  });

  const {ref} = useInViewWithQuery(notificationsQuery);

  useEffectOnce(() => {
    if (unseenNotificationCount) {
      markAllAsSeen();
    }
  });

  return (
    <div
      className={clsxm(
        "divide-neutral-white-dark divide-y bg-white",
        notificationsQuery.isLoading ? "flex h-full flex-col justify-center" : "",
      )}>
      {notificationsQuery.isLoading && (
        <Loader
          key="notificationInitial"
          styles={{
            loaderClassName: "size-10",
            containerClassName: "items-center h-full",
          }}
        />
      )}
      {notificationsQuery.data?.map((noti, index, array) => {
        const notificationType = mapNotification(noti.type);

        if (!notificationType) {
          return index === array.length - 1 ? <div ref={ref} /> : null;
        }

        const highlightValue = notificationType.titleHighlightParam
          ? noti.translation_params?.[notificationType.titleHighlightParam]
          : undefined;

        const actionUrl = notificationType.actionUrl?.(noti.action_params);
        const handleClick = () => {
          notificationType.onClick?.(noti.action_params);
          toggleOpen(false);
        };
        const rowClassName = clsxm(
          "flex min-h-fit items-center justify-between gap-10 px-5 py-4 text-left no-underline",
          noti.seen ? "bg-white" : "bg-soft-grey-100",
          "hover:bg-neutral-white-dark",
        );
        const rowContent = (
          <div className="flex flex-col justify-between gap-2">
            <div className="flex items-center gap-2">
              {notificationType.titleImage && noti.image_url && (
                <img src={noti.image_url} alt="" className="size-6 shrink-0 rounded-full object-cover" />
              )}
              <span className={clsxm("font-medium", notificationType.titleHighlightClassName)}>
                {highlightValue && highlightValue}{" "}
                <span className="text-neutral-black text-base font-medium">{noti.title}</span>
              </span>
            </div>
            <span className="text-neutral-grey-dark text-sm">{noti.body}</span>
            <div className="flex items-center gap-2">
              <div
                className={clsxm(
                  "rounded-full bg-linear-(--notification-gradient) p-px",
                  !notificationType.hasGradientBorder && "contents",
                )}>
                <div className={clsxm("rounded-full bg-white", !notificationType.hasGradientBorder && "contents")}>
                  <div
                    className={clsxm(
                      "bg-primary-light-solid flex items-center gap-1 rounded-full p-1.5",
                      notificationType.textClassName,
                    )}>
                    {notificationType.Icon && (
                      <notificationType.Icon className={clsxm("size-3 shrink-0", notificationType.iconClassName)} />
                    )}
                    <span className="text-2xs leading-none font-bold">
                      {t(notificationType.iconTextTranslation as "start_your_campaign_type")}
                    </span>
                  </div>
                </div>
              </div>
              <span className="text-neutral-grey-dark text-2xs p-0 leading-none">
                {dayjs.utc(noti.created).local().startOf("seconds").fromNow()}
              </span>
            </div>
          </div>
        );

        return (
          <Fragment key={noti.id}>
            {actionUrl ? (
              <Link to={actionUrl} onClick={handleClick} className={rowClassName}>
                {rowContent}
              </Link>
            ) : (
              <button onClick={handleClick} className={rowClassName} type="button">
                {rowContent}
              </button>
            )}
            {index === array.length - 1 && <div ref={ref} />}
          </Fragment>
        );
      })}
      {notificationsQuery.hasNextPage && (
        <div className="min-h-20">
          {notificationsQuery.isFetchingNextPage && (
            <Loader
              key="notifications"
              styles={{
                containerClassName: "items-center py-5",
                loaderClassName: "size-10",
              }}
            />
          )}
        </div>
      )}

      {!notificationsQuery.isLoading && notificationsQuery.data?.length === 0 && (
        <div className="bg-primary-light-solid mx-5 my-7 rounded-md p-6">
          <span className="my-0 bg-linear-(--brand-medium-gradient) bg-clip-text text-center text-xl font-bold text-transparent">
            {t("notification_tab_empty_state")}
          </span>
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
